import { type JanusCtx, type Row, activeStatuses, ecId, ecLean, itemField } from './context.js';
import { deadEffectTargetSkipReason } from './is-target-valid-for-effect.js';
import { type TriggerCandidate, type TriggerEvent } from '../reactions/trigger-dispatch.js';
import { runTriggerFamily, triggerFamilyPreDraw } from './trigger-executor.js';
import { itemTypeTriggerGap } from './caster-uses-item-type.js';
import { spellCircles } from './spell-circle-scope.js';

function itemTypeEvent(ctx: JanusCtx, ability: Row): TriggerEvent | null {
  const itemType = String(itemField(ability, 'item_type') ?? '');
  return itemType ? { trigger: 'triggers_when_caster_gets_item_type_against', itemType,
    spellLevel: itemType === 'spell' ? Number(ctx.read.value(ability, 'spell_level') ?? 0) : null,
    spellCircles: itemType === 'spell' ? spellCircles(ctx.read, ability) : [] } : null;
}
function targetItemTypeGap(ctx: JanusCtx, candidate: TriggerCandidate, event?: TriggerEvent): string | null {
  // The executor can grant resources/statuses, but cannot redirect the incoming spell or
  // project a newly granted spell immunity into that spell's already validated targets.
  for (const key of ['effect_is_reflected_back', 'immune_to_spells'])
    if (ctx.read.has(candidate.ability, key)) return `layer:${key}`;
  return itemTypeTriggerGap(ctx, candidate, event);
}
function uniqueTargets(ctx: JanusCtx, ability: Row, targets: Row[]): Row[] {
  const seen = new Set<string>();
  return targets.map(target => ctx.members.get(ecId(target)) ?? target).filter(target => {
    const id = ecId(target);
    if (!id || seen.has(id) || ctx.itemTypeAgainstFired?.has(id)) return false;
    // An effect the dead-target gate skips cannot provoke an incoming-use reaction.
    // Keep revives and non-damage corpse effects on the ordinary participant path.
    if (Number(ecLean(target, 'encounter_creature_dead') ?? 0) !== 0
      && deadEffectTargetSkipReason(ctx.read, ability, target, false, row => activeStatuses(ctx, row))) return false;
    seen.add(id); return true;
  });
}
/** Preview every due carrier before armor/attack dice; discovery and consent use the shared executor. */
export function itemTypeAgainstPreDraw(ctx: JanusCtx, ability: Row, caster: Row, targets: Row[]): void {
  const event = itemTypeEvent(ctx, ability);
  if (!event) return;
  for (const target of uniqueTargets(ctx, ability, targets)) triggerFamilyPreDraw(ctx, target, event, targetItemTypeGap, caster);
}
/** Once per target in this janus invocation, after armor/counterspell, before hit/save/effects. */
export function fireItemTypeAgainst(ctx: JanusCtx, ability: Row, caster: Row, targets: Row[]): void {
  const event = itemTypeEvent(ctx, ability);
  if (!event) return;
  const fired = ctx.itemTypeAgainstFired ??= new Set<string>();
  for (const target of uniqueTargets(ctx, ability, targets)) {
    fired.add(ecId(target));
    runTriggerFamily(ctx, event, target, caster, [target], false, targetItemTypeGap);
  }
}
